/**
 * PairingPrint - Figma-matching print renderer for pairing quizzes
 * Layout: PrintQuizTitle → main image → rows of [left box] ●──● [right box]
 * Problem is NOT displayed (per spec).
 * showCorrectAnswers: 각 좌측 dot에서 매칭되는 우측 dot까지 SVG 연결선을 그린다 (행 교차 가능).
 */

import { useRef, useState, useLayoutEffect, useCallback } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { PairingQuizData } from '../../../types/legacyQuiz'
import { PrintQuizTitle, PrintQuizImage, PrintOptionImage, getStyles, getImageUrl, getPrintScale } from './print-common'

interface PrintQuizProps {
  quizId: string | number
  quiz: PairingQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

// 교차 연결선 구분용 색상 팔레트
const LINE_COLORS = ['#ff9f19', '#0066CC', '#00BFA0', '#E0457B', '#7B61FF', '#F5A623']

interface ConnectorLine { x1: number; y1: number; x2: number; y2: number; color: string }

export function PairingPrint({
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const pairs = quiz.pairs
  const maxRows = Math.max(pairs.left.length, pairs.right.length)

  // 정답 매칭: leftIdx -> rightIdx (행 교차 가능). 매 렌더 계산(저렴) 후 ref로 보관.
  const matches: Array<[number, number]> = []
  if (showCorrectAnswers && correctAnswers) {
    correctAnswers.forEach((rightKey, leftIdx) => {
      if (!rightKey) return
      const rightIdx = pairs.right.findIndex(r => r.key === rightKey)
      if (rightIdx !== -1) matches.push([leftIdx, rightIdx])
    })
  }
  // effect 의존성용 안정 시그니처 (객체 identity 대신 값 기반 → 무한 루프 방지)
  const matchesSig = matches.map(([a, b]) => `${a}:${b}`).join('|')

  const gridRef = useRef<HTMLDivElement>(null)
  const leftDotRefs = useRef<Array<HTMLSpanElement | null>>([])
  const rightDotRefs = useRef<Array<HTMLSpanElement | null>>([])
  const matchesRef = useRef(matches)
  matchesRef.current = matches
  const [lines, setLines] = useState<ConnectorLine[]>([])

  const recompute = useCallback(() => {
    const grid = gridRef.current
    const m = matchesRef.current
    if (!grid || m.length === 0) {
      setLines(prev => (prev.length === 0 ? prev : []))
      return
    }
    const gridRect = grid.getBoundingClientRect()
    const next: ConnectorLine[] = []
    m.forEach(([leftIdx, rightIdx], i) => {
      const l = leftDotRefs.current[leftIdx]
      const r = rightDotRefs.current[rightIdx]
      if (!l || !r) return
      const lr = l.getBoundingClientRect()
      const rr = r.getBoundingClientRect()
      next.push({
        x1: lr.left + lr.width / 2 - gridRect.left,
        y1: lr.top + lr.height / 2 - gridRect.top,
        x2: rr.left + rr.width / 2 - gridRect.left,
        y2: rr.top + rr.height / 2 - gridRect.top,
        color: LINE_COLORS[i % LINE_COLORS.length],
      })
    })
    setLines(next)
  }, [])

  useLayoutEffect(() => {
    recompute()
    const grid = gridRef.current
    if (!grid || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => recompute())
    ro.observe(grid)
    return () => ro.disconnect()
  }, [recompute, matchesSig, maxRows, compact])

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Pairing rows — grid with 1fr rows for equal height */}
      <div
        ref={gridRef}
        className="w-full px-[40px]"
        style={{ position: 'relative', display: 'grid', gridTemplateColumns: '200px 1fr 200px', gridAutoRows: '1fr', gap: '12px 0' }}
      >
        {Array.from({ length: maxRows }).map((_, rowIdx) => {
          const leftItem = pairs.left[rowIdx]
          const rightItem = pairs.right[rowIdx]
          const leftImageUrl = leftItem?.image ? getImageUrl(leftItem.image) : null
          const rightImageUrl = rightItem?.image ? getImageUrl(rightItem.image) : null

          return (
            <div key={rowIdx} className="contents">
              {/* Left box (render only when leftItem exists) */}
              {leftItem ? (
                <div
                  className={cn(
                    'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px]',
                    'flex items-center justify-center min-h-[64px] px-[12px] py-[8px]'
                  )}
                >
                  {leftImageUrl && (
                    <PrintOptionImage src={leftImageUrl} alt="pairing-left" baseMaxHeightPx={28} />
                  )}
                  {leftItem.answer && (
                    <span className={cn('text-black', compact ? 'text-[15px]' : 'text-[14px]')}>
                      <KatexText text={leftItem.answer} />
                    </span>
                  )}
                </div>
              ) : (
                <div aria-hidden />
              )}

              {/* Dot connectors: left dot shown if leftItem exists, right dot if rightItem exists */}
              <div className="flex items-center justify-between px-[10px]">
                {leftItem ? (
                  <span ref={el => { leftDotRefs.current[rowIdx] = el }} className="size-[10px] rounded-full bg-[#ccc]" />
                ) : (
                  <span className="size-[10px]" aria-hidden />
                )}
                {rightItem ? (
                  <span ref={el => { rightDotRefs.current[rowIdx] = el }} className="size-[10px] rounded-full bg-[#ccc]" />
                ) : (
                  <span className="size-[10px]" aria-hidden />
                )}
              </div>

              {/* Right box (render only when rightItem exists) */}
              {rightItem ? (
                <div
                  className={cn(
                    'bg-white border-[#ccc] border-[1.5px] border-solid rounded-[6px]',
                    'flex items-center justify-center min-h-[64px] px-[12px] py-[8px]'
                  )}
                >
                  {rightImageUrl && (
                    <PrintOptionImage src={rightImageUrl} alt="pairing-right" baseMaxHeightPx={28} />
                  )}
                  {rightItem.answer && (
                    <span className={cn('text-black', compact ? 'text-[15px]' : 'text-[14px]')}>
                      <KatexText text={rightItem.answer} />
                    </span>
                  )}
                </div>
              ) : (
                <div aria-hidden />
              )}
            </div>
          )
        })}

        {/* SVG 연결선 오버레이 (정답 노출 시) */}
        {lines.length > 0 && (
          <svg
            aria-hidden
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}
          >
            {lines.map((ln, i) => (
              <line key={i} x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2} stroke={ln.color} strokeWidth={2} strokeLinecap="round" />
            ))}
          </svg>
        )}
      </div>
    </div>
  )
}
